
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/components/auth/AuthProvider";
import { LoginForm, LoginFormValues } from "@/components/auth/LoginForm";
import { RegistrationForm, RegistrationFormValues } from "@/components/auth/RegistrationForm";

const Login = () => {
  const [activeTab, setActiveTab] = useState("login");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  
  const { signIn, signUp } = useAuth();
  const navigate = useNavigate();

  const handleSignIn = async (values: LoginFormValues) => {
    setError("");
    setIsSubmitting(true);
    
    // Save credentials if remember me is checked
    if (values.rememberMe) {
      localStorage.setItem('rememberedCredentials', JSON.stringify({
        email: values.email,
        password: values.password
      }));
    } else {
      localStorage.removeItem('rememberedCredentials');
    }
    
    const { success, error } = await signIn(values.email, values.password);
    
    if (!success && error) {
      setError(error);
    }
    
    setIsSubmitting(false);
  };

  const handleSignUp = async (values: RegistrationFormValues) => {
    setError("");
    setIsSubmitting(true);
    
    const { success, error } = await signUp(values.email, values.password);
    
    if (success) {
      // Stay on registration tab to show success message
      // User will need to check email before they can sign in
    } else if (error) {
      setError(error);
    }
    
    setIsSubmitting(false);
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-64px)] bg-background px-4 py-8">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader>
          <CardTitle className="text-2xl text-center">Welcome to Habilou</CardTitle>
          <CardDescription className="text-center">
            Track your habits and achieve your goals
          </CardDescription>
        </CardHeader>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-2 w-full bg-muted/50 p-1 mb-4 rounded-full">
            <TabsTrigger 
              value="login"
              className="rounded-full py-1.5 px-4 text-sm data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-md transition-all duration-200 font-medium"
            >
              Login
            </TabsTrigger>
            <TabsTrigger 
              value="register"
              className="rounded-full py-1.5 px-4 text-sm data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-md transition-all duration-200 font-medium"
            >
              Register
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="login">
            <CardContent>
              <LoginForm onSubmit={handleSignIn} />
              {error && (
                <div className="text-destructive text-sm mt-4">{error}</div>
              )}
            </CardContent>
          </TabsContent>
          
          <TabsContent value="register">
            <CardContent>
              <RegistrationForm onSubmit={handleSignUp} />
              {error && (
                <div className="text-destructive text-sm mt-4">{error}</div>
              )}
            </CardContent>
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
};

export default Login;
